import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import ReactTable from 'react-table';
import { find, isEmpty, isEqual } from 'lodash';
import { cloneDeep } from 'lodash/lang';
import moment from 'moment/moment';

import { MANUAL_ENTRY } from 'components/pages/patient-source/Patients/PatientProfile/shared/ValueHistoryCells/constants';

import ModalBoxes from '../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import Button from '../../../../../../common/general/Button';
import Icon from '../../../../../../common/general/Icon';
import NotificationManager from '../../../../../../common/notifications/NotificationManager';
import { i18nt } from '../../../../../../root';
import { NS_COMMON, NSPP_PATIENT } from '../../../../../../services/i18n/namespaces';
import HistoryBlock, { MEDICAL_HISTORY } from '../../../../../root/Container/HistoryBlock';
import ReactTableHoc from '../../../shared/Worklist/ReactTableHoc/ReactTableHoc';
import CellDropdown from '../Medications/CellDropdown';
import { usePatientProfile } from '../PatientProfileContext';
import { convertToFirstDay } from '../service';
import { ReadOnlyCellTextField } from '../shared/ReadOnlyCellTextField/ReadOnlyCellTextField';
import ActionColumnContent from '../shared/TableActions/ActionColumnContent';
import TableDate from '../shared/TableDate/TableDate';
import { CellTextField } from '../shared/TableTextField/CellTextField';
import { OriginatingSourceCell, ValueHistoryDateCell } from '../shared/ValueHistoryCells/ValueHistoryCells';

import { MedicalHistoryAPI } from './MedicalHistoryAPI';

const MAX_COMMENT_LENGTH = 1000;

const newMedicalHistoryRecord = {
  diagnosis: null,
  startDate: '',
  endDate: '',
  onGoing: '',
  comments: '',
  id: null,
  newRow: true
};

export default function MedicalHistory() {
  const { t } = useTranslation();
  const { patientId } = useParams();
  const [data, setData] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [initialData, setInitialData] = useState([]);
  const [notSavedRow, setNotSavedRow] = useState({});
  const { userCanEdit } = usePatientProfile();

  const getPatientMedicalHistory = useCallback(
    function() {
      MedicalHistoryAPI.getPatientMedicalHistory(patientId).then(result => {
        setData(result.data);
        setInitialData(result.data);
      });
    },
    [patientId]
  );

  const getDiagnoses = useCallback(function() {
    MedicalHistoryAPI.getAllDiagnoses().then(result => {
      setDiagnoses(result.data);
    });
  }, []);

  useEffect(() => {
    getPatientMedicalHistory();
    getDiagnoses();
  }, [getDiagnoses, getPatientMedicalHistory]);

  useEffect(
    function() {
      const unblock = HistoryBlock.block(MEDICAL_HISTORY, function(discard) {
        if (!isEqual(data, initialData)) {
          showMedicalHistoryConfirmDialog()
            .then(() => {
              discard();
            })
            .catch(() => {});
        } else {
          return true;
        }
        return false;
      });
      return function() {
        unblock();
      };
    },
    [data, initialData]
  );

  const columns = [
    {
      Header: t(`${NSPP_PATIENT}:profile.medicalHistoryTab.diagnosis`),
      accessor: 'diagnosis',
      minWidth: 120,
      Cell: ({ original, value }) => {
        return userCanEdit ? (
          <CellDropdown value={value} onChange={e => onSelectChange(original, e, 'diagnosis')} options={diagnoses} />
        ) : (
          <ReadOnlyCellTextField value={value?.name} />
        );
      }
    },
    {
      Header: t(`${NS_COMMON}:startDate`),
      accessor: 'startDate',
      minWidth: 120,
      Cell: ({ value, original }) => {
        return userCanEdit ? (
          <TableDate
            value={value}
            placeholder={'Start date'}
            onChange={(e, formattedDate) => {
              onSelectChange(original, formattedDate, 'startDate');
            }}
          />
        ) : (
          <ReadOnlyCellTextField value={value} />
        );
      }
    },
    {
      Header: t(`${NS_COMMON}:endDate`),
      accessor: 'endDate',
      minWidth: 120,
      Cell: ({ value, original }) => {
        return userCanEdit ? (
          <TableDate
            value={value}
            placeholder={'End date'}
            onChange={(e, formattedDate) => {
              onSelectChange(original, formattedDate, 'endDate');
            }}
          />
        ) : (
          <ReadOnlyCellTextField value={value} />
        );
      }
    },
    {
      Header: t(`${NSPP_PATIENT}:ongoing`),
      accessor: 'onGoing',
      minWidth: 80,
      Cell: ({ original, value }) => {
        return userCanEdit ? (
          <CellDropdown
            value={getOngoingValue(value)}
            isDisabled={!isEmpty(original.endDate)}
            onChange={e => {
              const value = e.name === t('yes');
              onSelectChange(original, value, 'onGoing');
            }}
            options={[
              { id: 1, name: t('yes') },
              { id: 2, name: t('no') }
            ]}
          />
        ) : (
          <ReadOnlyCellTextField value={getOngoingValue(value)?.name} />
        );
      }
    },
    {
      Header: t(`${NS_COMMON}:comments`),
      accessor: 'comments',
      minWidth: 100,
      Cell: ({ original, value }) => {
        return userCanEdit ? (
          <CellTextField
            placeholder={t(`${NSPP_PATIENT}:profile.medicalHistoryTab.enterComment`)}
            onChange={e => {
              onSelectChange(original, e.target.value, 'comments');
            }}
            value={value}
          />
        ) : (
          <ReadOnlyCellTextField value={value} />
        );
      }
    },
    {
      Header: t(`${NS_COMMON}:dateAdded`),
      accessor: 'dateAdded',
      minWidth: 100,
      Cell: ({ value }) => <ValueHistoryDateCell value={value} />
    },
    {
      Header: t(`${NS_COMMON}:lastUpdated`),
      accessor: 'lastUpdated',
      minWidth: 110,
      Cell: ({ value }) => <ValueHistoryDateCell value={value} />
    },
    {
      Header: t(`${NS_COMMON}:originatingSource`),
      accessor: 'originatingSource',
      minWidth: 150,
      Cell: ({ value }) => <OriginatingSourceCell value={value} />
    }
  ];

  if (userCanEdit) {
    columns.push({
      Header: t(`${NSPP_PATIENT}:profile.medicalHistoryTab.actions`),
      width: 70,
      Cell: ({ original }) => {
        return (
          <ActionColumnContent
            onSaveButtonClick={() => saveRowData(original)}
            saveButtonDisabled={checkIfRowCanBeSaved(original)}
            onDeleteButtonClick={() => deleteMedication(original)}
          />
        );
      }
    });
  }

  return (
    <div className="patient-profile-medications">
      <div className="patient-profile-medications-header">
        <h5>{t(`${NSPP_PATIENT}:profile.medicalHistory`)}</h5>
        {userCanEdit && (
          <Button id="patient-profile-add-history-btn" onClick={addNewRow} priority="medium" size="h28">
            <Icon>add</Icon>
            {t('add')}
          </Button>
        )}
      </div>
      <div className="patient-profile-medications-table">
        <ReactTableHoc>
          <ReactTable
            data={data}
            columns={columns}
            minRows={1}
            showPagination={false}
            nextText=">>"
            previousText="<<"
            noDataText={t('noRecordFound')}
            pages={1}
            manual
          />
        </ReactTableHoc>
      </div>
    </div>
  );

  function deleteMedication(row) {
    ModalBoxes.confirm({
      title: t('confirmation'),
      content: t(`${NSPP_PATIENT}:profile.medicalHistoryTab.historyDeleteConfirmation`),
      confirmButton: t('yes'),
      cancelButton: t('cancel')
    })
      .then(() => {
        if (!row.newRow) {
          MedicalHistoryAPI.removePatientMedicalHistoryRecord(patientId, row.id).then(() => {
            getPatientMedicalHistory();
          });
        } else {
          setNotSavedRow({});
          setData(prevState => prevState.filter(item => !item.newRow));
        }
      })
      .catch(() => {});
  }

  function addNewRow() {
    if (isEmpty(notSavedRow)) {
      const draftRow = {
        ...newMedicalHistoryRecord,
        dateAdded: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        originatingSource: MANUAL_ENTRY
      };
      setData(prevState => [...prevState, draftRow]);
      setNotSavedRow(draftRow);
    } else {
      NotificationManager.error('Please save your previous changes');
    }
  }

  function saveRowData(row) {
    const payload = cloneDeep(row);
    if (!isEmpty(payload.endDate) && payload.onGoing) {
      payload.onGoing = false;
    }
    if (typeof payload.comments === 'string') {
      payload.comments = payload.comments.trim();
    }
    if (validateMedicationData(payload)) {
      if (payload.newRow) {
        MedicalHistoryAPI.addPatientMedicalHistoryRecord(patientId, payload)
          .then(result => {
            if (result.status === 200) {
              setNotSavedRow({});
              getPatientMedicalHistory();
            }
          })
          .catch(error => NotificationManager.error(error?.response?.data?.message));
      } else {
        MedicalHistoryAPI.editPatientMedicalHistoryRecord(patientId, payload)
          .then(result => {
            if (result.status === 200) {
              setNotSavedRow({});
              getPatientMedicalHistory();
            }
          })
          .catch(error => NotificationManager.error(error?.response?.data?.message));
      }
    }
  }

  function getOngoingValue(value) {
    if (value === true) {
      return { id: 1, name: t('yes') };
    }
    if (value === false) {
      return { id: 2, name: t('no') };
    } else {
      return null;
    }
  }

  function onSelectChange(row, newValue, key) {
    setData(function(prevState) {
      return prevState.map(obj => (obj.id === row.id ? { ...row, [key]: newValue } : obj));
    });
  }

  function checkIfRowCanBeSaved(row) {
    if (!row.id) return false;
    return isEqual(find(data, { id: row.id }), find(initialData, { id: row.id }));
  }

  function validateMedicationData(row) {
    const { startDate, endDate, onGoing, diagnosis, comments } = row;
    if (endDate && !startDate) {
      NotificationManager.error('If End date is entered, then Start date is required.');
      return false;
    }
    if (startDate && isEmpty(endDate) && !onGoing) {
      NotificationManager.error('If Start date is entered, then End date or Ongoing is required.');
      return false;
    }
    if (isEmpty(diagnosis)) {
      NotificationManager.error('Diagnosis is required.');
      return false;
    }
    if (
      moment(convertToFirstDay(startDate), 'DD/MMM/YYYY').isAfter(moment(convertToFirstDay(endDate), 'DD/MMM/YYYY'))
    ) {
      NotificationManager.error('End Date cannot be before Start Date is required.');
      return false;
    }
    if (diagnosis.name === 'See Comments' && !comments) {
      NotificationManager.error('A comment must be entered when See Comments is selected.');
      return false;
    }
    if (comments && comments.length > MAX_COMMENT_LENGTH) {
      NotificationManager.error(`Comment must not exceed ${MAX_COMMENT_LENGTH} characters.`);
      return false;
    }

    return true;
  }

  function showMedicalHistoryConfirmDialog() {
    return ModalBoxes.confirm({
      content: i18nt('profile.medicalHistoryTab.areYouSureToDiscardChanges', { ns: NSPP_PATIENT }),
      title: i18nt('profile.medicalHistoryTab.discardMedicalHistoryChanges', { ns: NSPP_PATIENT }),
      confirmButton: i18nt('yes'),
      cancelButton: i18nt('no')
    });
  }
}
